import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import {
  TAG_CELLS_PER_CALL, chunked, type ExpectedSite, type MissingTag, type MissingTagRow, type TagCellRef,
} from '../lib/missingBills';

// Missing bills by NMI (spec 2026-10-20): the enrolled sites and the active
// tags. Straight Supabase reads, paged at 1000 so the query count is
// ceil(N / 1000) and never one per site.

const PAGE_SIZE = 1000;

const SITE_COLUMNS = 'id, nmi, addr_street, addr_city, addr_state, current_retailer, bv_extn, client:client_id(name)';
const TAG_COLUMNS = 'id, site_id, month, tag, note, updated_by_email, updated_at';

// A site that is or ever was enrolled: anything but Not Enrolled, or one with an
// enrolment or unenrolment date. The grid decides month by month.
const ENROLLED_EVER = 'bv_extn->>bill_val_status.neq."Not Enrolled",bv_extn->>enrolment_date.not.is.null,bv_extn->>unenrolment_date.not.is.null';

function fail<T>(error: unknown): ApiResponse<T> {
  return { success: false, error: error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error) };
}

async function currentOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle();
  return (data?.organisation_id as string | undefined) ?? null;
}

export const missingBillService = {
  loadExpectedSites: async (): Promise<ApiResponse<ExpectedSite[]>> => {
    try {
      const orgId = await currentOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const all: ExpectedSite[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('site')
          .select(SITE_COLUMNS)
          .eq('org_id', orgId)
          .or(ENROLLED_EVER)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as unknown as ExpectedSite[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Active tags for months `from`..`to` (`YYYY-MM`). */
  loadActiveTags: async (from: string, to: string): Promise<ApiResponse<MissingTagRow[]>> => {
    try {
      const all: MissingTagRow[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_missing_bill_tag')
          .select(TAG_COLUMNS)
          .is('cleared_at', null)
          .gte('month', `${from}-01`)
          .lte('month', `${to}-01`)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as unknown as MissingTagRow[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * Tag the cells (migration 149 RPC). `note`: a string sets it ('' clears it),
   * null keeps each existing note. `changed` is how many existing tags changed.
   * Sent TAG_CELLS_PER_CALL cells per call, in order, counts summed; the first
   * failing call stops the rest, and the error says how many were saved.
   */
  setTag: async (
    cells: TagCellRef[],
    tag: MissingTag,
    note: string | null,
  ): Promise<ApiResponse<{ changed: number }>> => {
    let changed = 0;
    let saved = 0;
    try {
      for (const chunk of chunked(cells, TAG_CELLS_PER_CALL)) {
        const { data, error } = await supabase.rpc('bv_missing_bill_tag_set', {
          p_cells: chunk.map((c) => ({ site_id: c.siteId, month: `${c.month}-01` })),
          p_tag: tag,
          p_note: note,
        });
        if (error) throw error;
        changed += Number(data ?? 0);
        saved += chunk.length;
      }
      return { success: true, data: { changed } };
    } catch (error: unknown) {
      const res = fail<{ changed: number }>(error);
      if (saved > 0) res.error = `${res.error} (${saved} of ${cells.length} months were tagged before the error)`;
      return res;
    }
  },

  /**
   * Clear active tags by id (migration 149 RPC); the rows stay, with
   * `cleared_at` set. The server stamps who cleared them from the session.
   */
  clearTags: async (ids: string[]): Promise<ApiResponse<{ cleared: number }>> => {
    try {
      if (ids.length === 0) return { success: true, data: { cleared: 0 } };
      const { data, error } = await supabase.rpc('bv_missing_bill_tag_clear', { p_ids: ids });
      if (error) throw error;
      return { success: true, data: { cleared: Number(data ?? 0) } };
    } catch (error: unknown) {
      return fail(error);
    }
  },
};
